import { useNavigate } from 'react-router-dom';
import type { ReferralStatus } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useReferral } from '../hooks/useReferral';
import { useSession } from '../hooks/useSession';
import { money } from '../utils/money';
import { dayMonth } from '../utils/format';
import { InviteRow, ReferralHero, RulesList, StatTiles } from '../components/Growth';
import {
  Button,
  Card,
  DraftBar,
  EmptyState,
  Field,
  ListSkeleton,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui';

const STATUS_TONE: Record<ReferralStatus, 'positive' | 'negative' | 'neutral' | 'brand'> = {
  pending: 'brand',
  rewarded: 'positive',
  blocked: 'negative',
  expired: 'neutral',
};

/**
 * Refer & earn (W18): the hero with your code (copy) and Share invite (Web Share → native share →
 * copy), friends joined / waiting / earned, the monthly cap, your invites with their status, a
 * field for a friend's code, and how it works.
 */
export function ReferPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const r = useReferral(session.signedIn, {
    copied: t('refer.copied'),
    linkCopied: t('refer.linkCopied'),
    shareTitle: t('refer.shareTitle'),
    failed: t('common.wrong'),
  });
  const ref = r.referral;
  const amount = ref ? money(ref.country, ref.rewardMinor) : '';

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('refer.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-4">
        {r.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={r.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {r.error}
          </Notice>
        ) : r.loading || !ref ? (
          <Card>
            <ListSkeleton rows={4} tall />
          </Card>
        ) : (
          <>
            <ReferralHero
              title={t('refer.hero', { amount })}
              body={
                ref.friendOffer
                  ? t('refer.heroBody', { offer: ref.friendOffer.title, amount })
                  : t('refer.heroBodyNoOffer', { amount })
              }
              codeLabel={t('refer.yourCode')}
              code={ref.code}
              copyLabel={t('refer.copy')}
              onCopy={() => void r.copyCode()}
              shareLabel={t('refer.share')}
              onShare={r.share}
            />

            <StatTiles
              items={[
                { label: t('refer.friendsJoined'), value: String(ref.totals.invited) },
                { label: t('refer.waiting'), value: String(ref.totals.pending) },
                { label: t('refer.earned'), value: money(ref.country, ref.totals.earnedMinor) },
              ]}
            />
            <p className="-mt-3 text-center text-caption text-ink-3">
              {t('refer.monthCap', { used: ref.rewardsThisMonth, cap: ref.monthCap })}
            </p>

            <section>
              <SectionTitle>{t('refer.history')}</SectionTitle>
              {ref.history.length === 0 ? (
                <Card>
                  <EmptyState icon="users" title={t('refer.noHistory')} />
                </Card>
              ) : (
                <Card flush>
                  <div className="divide-y divide-hair">
                    {ref.history.map((row) => (
                      <InviteRow
                        key={`${row.friend}-${row.createdAt}`}
                        friend={row.friend}
                        status={t(`refer.status.${row.status}`)}
                        tone={STATUS_TONE[row.status]}
                        detail={dayMonth(row.rewardedAt ?? row.createdAt)}
                        reward={
                          row.status === 'rewarded' && row.rewardMinor > 0
                            ? `+ ${money(ref.country, row.rewardMinor)}`
                            : null
                        }
                      />
                    ))}
                  </div>
                </Card>
              )}
            </section>

            <section>
              <SectionTitle>{t('refer.haveCode')}</SectionTitle>
              <Card className="space-y-3">
                {r.draftRestored && (
                  <DraftBar
                    text={t('common.draftRestored')}
                    keepLabel={t('common.keep')}
                    discardLabel={t('common.discard')}
                    onKeep={r.keepDraft}
                    onDiscard={r.discardDraft}
                  />
                )}
                <div className="flex items-end gap-2">
                  <Field
                    className="min-w-0 flex-1"
                    label={t('refer.codeLabel')}
                    value={r.code}
                    onChange={(event) => r.setCode(event.target.value)}
                    autoCapitalize="characters"
                    autoComplete="off"
                    maxLength={16}
                    error={r.codeError}
                  />
                  <Button
                    tone="dark"
                    className={r.codeError ? 'mb-5' : ''}
                    busy={r.applying}
                    disabled={r.code.trim().length < 4}
                    onClick={r.applyCode}
                  >
                    {t('refer.apply')}
                  </Button>
                </div>
              </Card>
            </section>

            <RulesList
              title={t('refer.rules')}
              rules={[
                t('refer.rule1'),
                t('refer.rule2'),
                t('refer.rule3', { amount, days: ref.rewardDays }),
                t('refer.rule4', { cap: ref.monthCap }),
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
}
